import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { getIsSiteAdminFromQuery } from '@confluence/current-user/entry-points/getIsSiteAdminFromQuery';
import { getIsProductAdminFromQuery } from '@confluence/current-user/entry-points/getIsProductAdminFromQuery';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient';

import type { UseStaticContextProps } from './useStaticContext';
import useStaticContextDisplayConditionsSpaceAdminQuery, {
	type useStaticContextDisplayConditionsSpaceAdminQuery$data,
	type useStaticContextDisplayConditionsSpaceAdminQuery$variables,
} from './__generated__/useStaticContextDisplayConditionsSpaceAdminQuery.graphql';
import useStaticContextUserPrivilegeQuery, {
	type useStaticContextUserPrivilegeQuery$data,
	type useStaticContextUserPrivilegeQuery$variables,
} from './__generated__/useStaticContextUserPrivilegeQuery.graphql';

export async function preloadUseStaticContext({ spaceKey }: Partial<UseStaticContextProps>) {
	const { isLoggedIn } = getPreloaderFnContext();

	try {
		// eslint-disable-next-line check-preloaders/no-promise-all
		const [{ data: spaceAdminData }, { data: privilegeData }] = await Promise.all([
			query<
				useStaticContextDisplayConditionsSpaceAdminQuery$data,
				useStaticContextDisplayConditionsSpaceAdminQuery$variables
			>({
				query: useStaticContextDisplayConditionsSpaceAdminQuery,
				variables: { spaceKey: spaceKey ?? '' },
			}),
			query<useStaticContextUserPrivilegeQuery$data, useStaticContextUserPrivilegeQuery$variables>({
				query: useStaticContextUserPrivilegeQuery,
			}),
		]);

		const isSpaceAdmin = (spaceAdminData as any)?.space?.currentUser?.isAdmin;
		const isSiteAdmin = getIsSiteAdminFromQuery(privilegeData);
		const isAdmin = isSiteAdmin || getIsProductAdminFromQuery(privilegeData);
		return {
			isSpaceAdmin,
			isLoggedIn,
			isAdmin,
			isSiteAdmin,
		};
	} catch (e) {
		ssrPrepareErrorsClient.submitError(
			new Error(`Cannot preload data for useStaticContext, using defaults for SSR: ${e.message}`),
		);

		return {
			isLoggedIn,
			isSpaceAdmin: false,
			isAdmin: false,
			isSiteAdmin: false,
		};
	}
}
